/**
 * Statistics dashboard service — one aggregated fetch for the whole page.
 * `GET /account/statistics-overview?from&to&channel` (Admin API ≥ 1.0.8).
 */

import { httpClient } from '@/utils/httpClient';
import type { NotifyEnvelope } from '@/types/api';

export interface StatisticsKpis {
  totalSent: number;
  failed: number;
  successRate: number | null;
  activeAccounts: number;
  prevTotalSent: number;
  prevFailed: number;
  prevSuccessRate: number | null;
  prevActiveAccounts: number;
  avgProcessingMs: number | null;
  p95ProcessingMs: number | null;
  prevAvgProcessingMs: number | null;
}

export interface StatisticsDailyPoint {
  date: string;
  byChannel: Record<string, number>;
  failed: number;
  total: number;
}

export interface StatisticsChannelRow {
  channel: string;
  sent: number;
  failed: number;
  successRate: number | null;
  avgProcessingMs: number | null;
  avgDeliveryMs: number | null;
}

export interface StatisticsAccountRow {
  accountId: string;
  accountName: string;
  requests: number;
  errors: number;
  successRate: number | null;
}

export interface StatisticsApiErrorRow {
  accountName: string;
  endpoint: string;
  errorsLast24h: number;
  errorsInPeriod: number;
  okInPeriod: number;
}

export interface StatisticsOverview {
  kpis: StatisticsKpis;
  daily: StatisticsDailyPoint[];
  byChannel: StatisticsChannelRow[];
  topAccounts: StatisticsAccountRow[];
  apiErrors: StatisticsApiErrorRow[];
}

export const getStatisticsOverview = async (
  from: string,
  to: string,
  channel?: string,
  accountId?: string
): Promise<StatisticsOverview> => {
  const qs = new URLSearchParams({ from, to });
  if (channel) qs.set('channel', channel);
  if (accountId) qs.set('accountId', accountId);
  const { data } = await httpClient.get<NotifyEnvelope<StatisticsOverview>>(
    `/account/statistics-overview?${qs.toString()}`,
    { skipGlobalLoader: true }
  );
  return data.Response;
};
